import { useState, useEffect } from "react";
import API from "../lib/api";
import { useNavigate } from "react-router-dom";
import { Wifi, WifiOff, Lock, Unlock, MapPin, Clock, Loader2, Smartphone, AlertCircle, ChevronDown } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";

export default function DashboardPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState("");
  const [actionMsg, setActionMsg] = useState("");
  const [activeChildId, setActiveChildId] = useState(null);

  useEffect(() => {
    fetchDashboard();
  }, [activeChildId]);

  const fetchDashboard = async () => {
    try {
      const params = activeChildId ? `?child_id=${activeChildId}` : "";
      const { data: d } = await API.get(`/dashboard${params}`);
      setData(d);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeviceAction = async (action) => {
    setActionLoading(action);
    setActionMsg("");
    try {
      const { data: res } = await API.post("/device/action", {
        action,
        child_id: data?.child?.child_id || activeChildId
      });
      setData((prev) => ({
        ...prev,
        child: { ...prev.child, device_status: res.device_status },
      }));
      if (res.message) setActionMsg(res.message);
      setTimeout(() => setActionMsg(""), 4000);
    } catch (err) {
      console.error(err);
    } finally {
      setActionLoading("");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-[#FF5C39] animate-spin" />
      </div>
    );
  }

  const child = data?.child;
  const children = data?.children || [];
  const policy = data?.screen_time;
  const device = data?.device;
  const hasDevice = device !== null;
  const hasMultipleChildren = children.length > 1;
  const usedMinutes = policy?.used_today_minutes || 0;
  const limitMinutes = policy?.daily_limit_minutes || 120;
  const remaining = Math.max(0, limitMinutes - usedMinutes);
  const pctUsed = limitMinutes > 0 ? Math.round((usedMinutes / limitMinutes) * 100) : 0;

  const chartData = [
    { name: "Used", value: usedMinutes },
    { name: "Remaining", value: remaining },
  ];

  const isNoDevice = child?.device_status === "no_device";
  const statusColor = isNoDevice ? "#FFD23F" : child?.device_status === "online" ? "#34D399" : child?.device_status === "locked" ? "#F43F5E" : "#52526B";
  const statusLabel = isNoDevice ? "No device yet" : child?.device_status === "online" ? "Online" : child?.device_status === "locked" ? "Locked" : "Offline";

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl md:text-3xl font-extrabold text-[#1F1F2E] tracking-tight">
          Good {new Date().getHours() < 12 ? "morning" : new Date().getHours() < 18 ? "afternoon" : "evening"}!
        </h1>
        {/* Child selector for multi-child accounts */}
        {hasMultipleChildren && (
          <select
            value={activeChildId || child?.child_id || ""}
            onChange={(e) => { setActiveChildId(e.target.value); setLoading(true); }}
            data-testid="child-selector"
            className="bg-white border-2 border-[#F0E4D8] rounded-2xl px-4 py-2 text-sm font-bold text-[#1F1F2E] outline-none focus:border-[#4D96FF] transition-all"
          >
            {children.map((c) => (
              <option key={c.child_id} value={c.child_id}>
                {c.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* No device enrolled banner */}
      {isNoDevice && (
        <div className="bg-[#FFD23F]/15 border-2 border-[#FFD23F]/40 rounded-2xl p-5 flex items-start gap-4" data-testid="no-device-banner">
          <div className="w-12 h-12 bg-[#FFD23F]/20 rounded-xl flex items-center justify-center flex-shrink-0">
            <Smartphone className="w-6 h-6 text-[#FF5C39]" strokeWidth={2.5} />
          </div>
          <div>
            <p className="font-extrabold text-[#1F1F2E]">Waiting for your child's phone</p>
            <p className="text-sm text-[#52526B] font-semibold mt-1">
              Once you receive and set up the Alfie phone, it will appear here automatically. 
              You'll be able to see their location, manage apps, and control screen time.
            </p>
          </div>
        </div>
      )}

      {/* Child Profile Card */}
      <div className="bg-white rounded-[32px] p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-[#F0E4D8]/50" data-testid="child-profile-card">
        <div className="flex items-center gap-4">
          <img
            src={child?.photo_url || "https://images.unsplash.com/photo-1597113117918-eb40fe0b84e7?w=200"}
            alt={child?.name}
            className="w-20 h-20 rounded-full object-cover border-4 border-[#FFD23F]"
            data-testid="child-avatar"
          />
          <div className="flex-1">
            <h2 className="text-xl font-extrabold text-[#1F1F2E]" data-testid="child-name">
              {child?.name || "Your child"}
            </h2>
            <p className="text-sm font-semibold text-[#52526B]">Age {child?.age || "?"}</p>
            <div className="mt-1 flex items-center gap-1.5" data-testid="device-status">
              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: statusColor }} />
              <span className="text-sm font-bold" style={{ color: statusColor }}>
                {statusLabel}
              </span>
            </div>
            {/* Real device info */}
            {hasDevice && (
              <p className="text-xs text-[#52526B] mt-1">
                {device.model} {device.battery_level != null ? `| ${device.battery_level}% battery` : ""}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Screen Time Card */}
      <div className="bg-white rounded-[32px] p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-[#F0E4D8]/50" data-testid="screen-time-card">
        <h3 className="text-sm font-bold uppercase tracking-wider text-[#52526B] mb-4">Today's screen time</h3>
        <div className="flex items-center gap-6">
          <div className="w-28 h-28 relative">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={34}
                  outerRadius={48}
                  startAngle={90}
                  endAngle={-270}
                  dataKey="value"
                  strokeWidth={0}
                >
                  <Cell fill="#FF5C39" />
                  <Cell fill="#F0E4D8" />
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-lg font-extrabold text-[#1F1F2E]">{pctUsed}%</span>
            </div>
          </div>
          <div className="flex-1">
            <p className="text-2xl font-extrabold text-[#1F1F2E]">
              {Math.floor(usedMinutes / 60)}h {usedMinutes % 60}m
              <span className="text-sm font-bold text-[#52526B] ml-1">used</span>
            </p>
            <p className="text-sm font-semibold text-[#52526B] mt-1">
              of {Math.floor(limitMinutes / 60)}h {limitMinutes % 60}m daily limit
            </p>
            <p className="text-xs font-bold text-[#4D96FF] mt-2 flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" strokeWidth={3} />
              {policy?.mode_label || "Free Time"} active
            </p>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div>
        <h3 className="text-sm font-bold uppercase tracking-wider text-[#52526B] mb-3">Quick actions</h3>
        <div className="grid grid-cols-3 gap-3">
          <button
            onClick={() => handleDeviceAction("lock")}
            disabled={!!actionLoading}
            data-testid="quick-action-lock"
            className="bg-[#FFF0E0] text-[#FF5C39] font-bold rounded-2xl p-4 flex flex-col items-center gap-2 hover:bg-[#FFD23F]/20 active:scale-95 transition-all"
          >
            {actionLoading === "lock" ? (
              <Loader2 className="w-8 h-8 animate-spin" />
            ) : (
              <Lock className="w-8 h-8" strokeWidth={2.5} />
            )}
            <span className="text-xs">Lock device</span>
          </button>
          <button
            onClick={() => handleDeviceAction("unlock")}
            disabled={!!actionLoading}
            data-testid="quick-action-unlock"
            className="bg-[#FFF0E0] text-[#FF5C39] font-bold rounded-2xl p-4 flex flex-col items-center gap-2 hover:bg-[#FFD23F]/20 active:scale-95 transition-all"
          >
            {actionLoading === "unlock" ? (
              <Loader2 className="w-8 h-8 animate-spin" />
            ) : (
              <Unlock className="w-8 h-8" strokeWidth={2.5} />
            )}
            <span className="text-xs">Unlock</span>
          </button>
          <button
            onClick={() => navigate("/location")}
            data-testid="quick-action-location"
            className="bg-[#FFF0E0] text-[#FF5C39] font-bold rounded-2xl p-4 flex flex-col items-center gap-2 hover:bg-[#FFD23F]/20 active:scale-95 transition-all"
          >
            <MapPin className="w-8 h-8" strokeWidth={2.5} />
            <span className="text-xs">Location</span>
          </button>
        </div>
      </div>

      {/* Action feedback message */}
      {actionMsg && (
        <div className="bg-[#4D96FF]/10 text-[#4D96FF] px-4 py-3 rounded-2xl font-bold text-sm flex items-center gap-2 animate-fade-in" data-testid="action-message">
          <AlertCircle className="w-4 h-4 flex-shrink-0" strokeWidth={2.5} />
          {actionMsg}
        </div>
      )}

      {/* Offline / no device message */}
      {(child?.device_status === "offline" || isNoDevice) && !actionMsg && (
        <div className="bg-[#FFF0E0] rounded-2xl p-4 flex items-start gap-3" data-testid="offline-message">
          <WifiOff className="w-5 h-5 text-[#52526B] mt-0.5 flex-shrink-0" strokeWidth={2.5} />
          <div>
            <p className="text-sm font-bold text-[#1F1F2E]">
              {isNoDevice ? "No device connected" : "Device is offline"}
            </p>
            <p className="text-xs text-[#52526B] font-semibold mt-0.5">
              {isNoDevice
                ? "Once your child's Alfie phone is set up, you'll see their status here."
                : `${child?.name}'s phone isn't connected right now. Actions will take effect when it comes back online.`}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
